import React from 'react';
import { Alert } from '@itandi/itandi-bb-ui';
import type { ComponentProps } from 'react';
import { toast } from 'react-toastify';
import Link from 'next/link';
import Styles from './Page.module.scss';
import { useDeals } from '~/hooks/useDeals';
import { useDealConstructionItems } from '~/hooks/useDealConstructionItems';
import Loading from '~/components/common/parts/loading/Loading';
import { useDealBillings } from '~/hooks/deals/useDealBillings';
import { PATH as DEALS_PATH } from '~/pages/deals';
import { PATH } from '~/pages/deals/[id]';
import { DefaultV2 } from '~/components/common/layouts/DefaultV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { ConstructionTop } from '~/components/common/constructions/top/ConstructionTop';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import type {
  DealConstruction,
  DealConstructionEdit
} from '~/models/DealConstruction';
import type {
  DealSettlement,
  DealSettlementEdit
} from '~/models/DealSettlement';
import { getDownloadPath } from '~/utils/api/deals/DealBillingFile';
import type { NskResponse } from '~/models/Api';
import {
  create as postDealBilling,
  destroy as destroyBilling,
  update as updateDealBilling,
  updateSeq
} from '~/utils/api/deals/DealBilling';
import { ChangeAmountModal } from '~/components/common/constructions/change_amount/ChangeAmountModal';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { InvoiceItemAddForm } from '~/components/common/constructions/adding/InvoiceItemAddForm';
import { SettlementList } from '~/components/common/settlements/list/SettlementList';
import { useUsingApplications } from '~/hooks/useUsingApplications';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { create as generateBillings } from '~/utils/api/deals/DealBillingGenerate';
import { isSuccess } from '~/utils/api/api_handler';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { isOwnStatus } from '~/models/Status';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { updateAccept } from '~/utils/api/deals/DealEstimationHeader';

export const Page: React.FC = () => {
  const [showChangeAmountModal, setShowChangeAmountModal] =
    React.useState<boolean>(false);
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const id = useIdQueryHandler();
  const { data: deal } = useDeals().show(id);
  const { data: dealConstructionItems } = useDealConstructionItems();
  const { data: dealEstimationHistoryContext } =
    useDealEstimationHistories().index(id);
  const { useSK } = useUsingApplications();
  const { data: billingContext, mutate: mutateBillings } =
    useDealBillings().index(id, 'owner');
  if (
    deal === null ||
    billingContext === null ||
    dealConstructionItems === null ||
    dealEstimationHistoryContext === null
  ) {
    return <Loading isLoading />;
  }
  const estimationHeaderId =
    dealEstimationHistoryContext.dealEstimationHeaderId;
  const pageTitle = useSK
    ? 'オーナーへの請求書作成'
    : 'オーナーへの請求書・精算書作成';
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal?.id), as: Link },
    { label: pageTitle }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  const handleClickPreview = (): void => {
    if (id === null) return;
    const path = getDownloadPath(id, 'deal_billing_owner');
    window.open(path);
  };
  const handleMutateConstructions = async (): Promise<void> => {
    await mutateBillings?.();
  };
  const handleCreate = async (
    submitValue: DealConstructionEdit | DealSettlementEdit
  ): Promise<NskResponse<void>> =>
    postDealBilling(submitValue, deal.id, 'owner');
  const handleUpdate = async (
    submitValue: DealConstructionEdit | DealSettlementEdit,
    billingId: number | null
  ): Promise<NskResponse<void>> => {
    if (billingId === null) {
      throw new Error('billingId cannot be null');
    }
    return updateDealBilling(submitValue, billingId, deal.id);
  };
  const handleDestroy = async (
    deleteBilling: DealConstruction | DealSettlement
  ): Promise<NskResponse<void>> => {
    if (deleteBilling.type !== 'DealBilling') {
      throw new Error(`Illegal construction type: ${deleteBilling.type}`);
    }
    if (deleteBilling.billingType !== 'owner') {
      throw new Error(`Illegal billing type: ${deleteBilling.billingType}`);
    }
    return destroyBilling(deleteBilling, deal.id);
  };
  const handleUpdateSeq = async (
    newBillings: DealConstruction[] | DealSettlement[]
  ): Promise<void> => {
    await updateSeq(newBillings, deal.id);
  };
  const handleClickGenerateBillings = async (): Promise<void> => {
    // 非共有案件で申請前(draftのheader)が存在する場合は承認させる
    // https://itandi-dev.atlassian.net/browse/NSKDEV-1622対応
    if (isOwnStatus(deal.statusId) && estimationHeaderId !== null) {
      const response = await updateAccept(deal.id, estimationHeaderId);
      if (!isSuccess(response)) {
        toast.error('エラーが発生しました');
        handleClose();
        return;
      }
    }
    const result = await generateBillings(deal.id, 'owner');
    if (isSuccess(result)) {
      await mutateBillings?.();
      toast.success('請求を再作成しました');
      handleClose();
      return;
    }
    toast.error('エラーが発生しました');
    handleClose();
  };
  return (
    <DefaultV2>
      <DealLayout deal={deal} isVendor={false} items={items}>
        <h2 className={Styles.EstimateTitle}>{pageTitle}</h2>
        <ConstructionTop
          billingTarget="owner"
          constructionContext={billingContext}
          handleClickBillingPreview={handleClickPreview}
          handleClickChangeAmount={(): void => setShowChangeAmountModal(true)}
          handleClickGenerateBillings={handleOpen}
          readonly={false}
          useSK={useSK}
        />
        {showChangeAmountModal && (
          <ChangeAmountModal
            billingContext={billingContext}
            billingType="owner"
            handleClose={(): void => setShowChangeAmountModal(false)}
          />
        )}
        {isOpen && (
          <ConfirmModal
            confirmType="save"
            handleAccept={handleClickGenerateBillings}
            handleCancel={handleClose}
            isOpen={isOpen}
          >
            工事見積りから請求を再作成しますか？
            <Alert marginTop={8}>
              現在登録されているオーナーへの請求情報は削除されます
            </Alert>
          </ConfirmModal>
        )}
        <div className={Styles.Container}>
          <div className={Styles.EstimateAddForm}>
            <InvoiceItemAddForm
              billingType="owner"
              handleCreate={handleCreate}
              mutateConstructions={handleMutateConstructions}
            />
          </div>
          <div className={Styles.EstimateList}>
            {billingContext.dealSettlements.length > 0 && (
              <SettlementList
                constructionContext={billingContext}
                handleDestroy={handleDestroy}
                handleMutateSettlements={handleMutateConstructions}
                handleUpdate={handleUpdate}
                handleUpdateSeq={handleUpdateSeq}
                target="owner"
              />
            )}
            <ConstructionList
              companyType="client"
              constructionContext={billingContext}
              deal={deal}
              dealConstructionItems={dealConstructionItems}
              docType="billing"
              handleDestroy={handleDestroy}
              handleMutateConstructions={handleMutateConstructions}
              handleUpdate={handleUpdate}
              handleUpdateSeq={handleUpdateSeq}
              readonly={false}
              target="owner"
            />
          </div>
        </div>
      </DealLayout>
    </DefaultV2>
  );
};
